import React, { createContext, useContext, useState, useEffect } from 'react';
import enTranslations from '../translations/en';
import frTranslations from '../translations/fr';
import { detectPreferredLanguage } from '../utils/language';

type Language = 'en' | 'fr';
type TranslationKey = string;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string | string[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(detectPreferredLanguage);

  const translations = {
    en: enTranslations,
    fr: frTranslations,
  };

  // Auto-redirect to preferred language on first visit (only if no explicit language in URL)
  useEffect(() => {
    const currentPath = window.location.pathname;
    const urlLang = currentPath.split('/')[1];
    
    // Only redirect if we're at the root or /en (default route)
    if (currentPath === '/' || currentPath === '/en') {
      const preferredLang = detectPreferredLanguage();
      
      // If preferred language is different from current, redirect
      if (preferredLang !== 'en' && preferredLang !== urlLang) {
        const newPath = currentPath === '/' ? `/${preferredLang}` : currentPath.replace('/en', `/${preferredLang}`);
        window.location.href = newPath;
        return;
      }
    }
  }, []); // Only run once on mount

  // Update URL when language changes
  useEffect(() => {
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace(/^\/(en|fr)/, `/${language}`);
    if (currentPath !== newPath) {
      window.history.replaceState(null, '', newPath);
    }
  }, [language]);

  const t = (key: TranslationKey): string | string[] => {
    const translation = translations[language];
    const result = key.split('.').reduce((obj, key) => obj?.[key], translation);
    
    // Handle different return types
    if (typeof result === 'string' || Array.isArray(result)) {
      return result;
    }
    
    // If the key doesn't exist or returns an object, return the key itself
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 